import { useState } from 'react';
import {
  XMarkIcon,
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowPathIcon,
  SignalIcon,
  PlayIcon,
  StopIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import type { SocialEvent, EventStatus } from './types';
import type { ClubTheme } from './themes';
import { useAuth } from '@/lib/auth-context';
import {
  useEventDetail,
  useUpdateEventStatus,
  useSpawnNextEvent,
  useRsvp,
  useCancelRsvp,
} from './queries';
import EventArtifactsPanel from './EventArtifactsPanel';
import LiveKitRoom from './LiveKitRoom';

interface EventDetailModalProps {
  clubId: string;
  eventId: string;
  theme: ClubTheme;
  onClose: () => void;
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const STATUS_CONFIG: Record<EventStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: '#9ca3af' },
  pending: { label: 'Pending Approval', color: '#f59e0b' },
  scheduled: { label: 'Scheduled', color: '#3b82f6' },
  live: { label: 'Live Now', color: '#22c55e' },
  completed: { label: 'Completed', color: '#6b7280' },
  cancelled: { label: 'Cancelled', color: '#ef4444' },
};

const RECURRENCE_LABELS: Record<string, string> = {
  weekly: 'Every week',
  biweekly: 'Every 2 weeks',
  monthly: 'Every month',
};

export default function EventDetailModal({ clubId, eventId, theme, onClose }: EventDetailModalProps) {
  const { user } = useAuth();
  const { data: event, isLoading } = useEventDetail(clubId, eventId);
  const updateStatus = useUpdateEventStatus(clubId, eventId);
  const spawnNext = useSpawnNextEvent(clubId, eventId);
  const rsvp = useRsvp(clubId, eventId);
  const cancelRsvp = useCancelRsvp(clubId, eventId);
  const [showLiveKit, setShowLiveKit] = useState(false);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-t-transparent"
          style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }} />
      </div>
    );
  }

  if (!event) return null;

  const statusConf = STATUS_CONFIG[event.status];
  const isUpcoming = new Date(event.starts_at) > new Date();
  const isCreator = user?.id === event.created_by;
  const canEdit = isCreator; // Only the creator can manage the event

  const handleRsvp = (status: 'going' | 'interested') => {
    if (event.my_rsvp === status) {
      cancelRsvp.mutate();
    } else {
      rsvp.mutate(status);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl p-6"
        style={{ backgroundColor: theme.colors.surface }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button type="button" onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 transition-colors hover:bg-opacity-20"
          style={{ color: theme.colors.muted }}>
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Status badge */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold"
            style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}>
            {event.status === 'live' && <SignalIcon className="h-3.5 w-3.5 animate-pulse" />}
            {statusConf.label}
          </span>
          {event.recurrence && (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: theme.colors.muted }}>
              <ArrowPathIcon className="h-3.5 w-3.5" />
              {RECURRENCE_LABELS[event.recurrence]}
            </span>
          )}
          {event.credits_entry && Number(event.credits_entry) > 0 && (
            <span className="text-xs font-medium" style={{ color: theme.colors.primary }}>
              {event.credits_entry} credits to attend
            </span>
          )}
        </div>

        {/* Title + description */}
        <h2 className="text-xl font-bold mb-1" style={{ color: theme.colors.text }}>
          {event.title}
        </h2>
        {event.description && (
          <p className="text-sm leading-relaxed mb-4" style={{ color: theme.colors.muted }}>
            {event.description}
          </p>
        )}

        {/* Date, time, location */}
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: theme.colors.text }}>
            <CalendarIcon className="h-4 w-4 shrink-0" style={{ color: theme.colors.muted }} />
            {formatDateTime(event.starts_at)}
          </div>
          {event.ends_at && (
            <div className="flex items-center gap-2 text-sm" style={{ color: theme.colors.text }}>
              <ClockIcon className="h-4 w-4 shrink-0" style={{ color: theme.colors.muted }} />
              Ends {formatDateTime(event.ends_at)}
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-2 text-sm" style={{ color: theme.colors.text }}>
              <MapPinIcon className="h-4 w-4 shrink-0" style={{ color: theme.colors.muted }} />
              {event.location}
            </div>
          )}
        </div>

        {/* Attendees */}
        <div className="flex items-center gap-2 text-sm mb-4" style={{ color: theme.colors.muted }}>
          <UserGroupIcon className="h-4 w-4" />
          <span>{event.rsvp_counts.going} going, {event.rsvp_counts.interested} interested</span>
          {event.max_attendees && <span> / {event.max_attendees} max</span>}
        </div>

        {/* RSVP buttons — hidden for the event creator (they're the host) */}
        {!isCreator && (isUpcoming || event.status === 'live') && event.status !== 'cancelled' && event.status !== 'pending' && (
          <div className="flex gap-2 mb-4">
            <button type="button" onClick={() => handleRsvp('going')}
              disabled={rsvp.isPending || cancelRsvp.isPending}
              className="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
              style={{
                backgroundColor: event.my_rsvp === 'going' ? theme.colors.primary : `${theme.colors.primary}18`,
                color: event.my_rsvp === 'going' ? theme.colors.bg : theme.colors.primary,
              }}>
              {event.my_rsvp === 'going'
                ? 'Going'
                : event.credits_entry && Number(event.credits_entry) > 0
                  ? `I'm Going (${event.credits_entry} cr)`
                  : "I'm Going"}
            </button>
            <button type="button" onClick={() => handleRsvp('interested')}
              disabled={rsvp.isPending || cancelRsvp.isPending}
              className="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
              style={{
                backgroundColor: event.my_rsvp === 'interested' ? theme.colors.accent : `${theme.colors.muted}15`,
                color: event.my_rsvp === 'interested' ? theme.colors.bg : theme.colors.muted,
              }}>
              {event.my_rsvp === 'interested' ? 'Interested' : 'Interested?'}
            </button>
          </div>
        )}

        {/* Host badge for creator */}
        {isCreator && (
          <div className="mb-4">
            <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold"
              style={{ backgroundColor: `${theme.colors.primary}15`, color: theme.colors.primary }}>
              You're hosting this event
            </span>
          </div>
        )}

        {/* Status controls — only visible to the event creator */}
        {canEdit && (
          <div className="flex flex-wrap gap-2 mb-4">
            {event.status === 'scheduled' && (
              <button type="button"
                onClick={() => updateStatus.mutate('live')}
                disabled={updateStatus.isPending}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ backgroundColor: '#22c55e20', color: '#22c55e' }}>
                <PlayIcon className="h-3.5 w-3.5" /> Go Live
              </button>
            )}
            {event.status === 'live' && (
              <button type="button"
                onClick={() => updateStatus.mutate('completed')}
                disabled={updateStatus.isPending}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ backgroundColor: '#6b728020', color: '#6b7280' }}>
                <StopIcon className="h-3.5 w-3.5" /> End Event
              </button>
            )}
            {event.status === 'pending' && (
              <button type="button"
                onClick={() => updateStatus.mutate('scheduled')}
                disabled={updateStatus.isPending}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ backgroundColor: '#3b82f620', color: '#3b82f6' }}>
                <CheckCircleIcon className="h-3.5 w-3.5" /> Approve
              </button>
            )}
            {['scheduled', 'pending'].includes(event.status) && (
              <button type="button"
                onClick={() => updateStatus.mutate('cancelled')}
                disabled={updateStatus.isPending}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ backgroundColor: '#ef444420', color: '#ef4444' }}>
                <XCircleIcon className="h-3.5 w-3.5" /> Cancel
              </button>
            )}
            {event.recurrence && event.status === 'completed' && (
              <button type="button"
                onClick={() => spawnNext.mutate()}
                disabled={spawnNext.isPending}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ backgroundColor: `${theme.colors.primary}20`, color: theme.colors.primary }}>
                <ArrowRightIcon className="h-3.5 w-3.5" />
                {spawnNext.isPending ? 'Scheduling...' : 'Schedule Next'}
              </button>
            )}
          </div>
        )}

        {/* LiveKit video room — creator can always join, others need RSVP */}
        {event.status === 'live' && event.livekit_room_id && (
          <div className="mb-4">
            {(isCreator || event.my_rsvp === 'going') ? (
              showLiveKit ? (
                <LiveKitRoom
                  clubId={clubId}
                  eventId={eventId}
                  theme={theme}
                  onLeave={() => setShowLiveKit(false)}
                />
              ) : (
                <button type="button"
                  onClick={() => setShowLiveKit(true)}
                  className="w-full rounded-xl p-4 text-center font-semibold transition-colors"
                  style={{ backgroundColor: '#22c55e20', color: '#22c55e', border: '1px solid #22c55e40' }}>
                  Join Video Room
                </button>
              )
            ) : (
              <div className="rounded-xl p-4 text-center text-sm"
                style={{ backgroundColor: `${theme.colors.muted}10`, color: theme.colors.muted, border: `1px solid ${theme.colors.muted}20` }}>
                RSVP as "Going" to join the video room
              </div>
            )}
          </div>
        )}

        {/* Series timeline */}
        {event.series_instances && event.series_instances.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: theme.colors.muted }}>
              Upcoming in Series
            </h4>
            <div className="space-y-1">
              {event.series_instances.map((inst) => (
                <div key={inst.id} className="flex items-center gap-2 text-xs" style={{ color: theme.colors.text }}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: STATUS_CONFIG[inst.status]?.color || '#6b7280' }} />
                  <span>{inst.title}</span>
                  <span style={{ color: theme.colors.muted }}>
                    {new Date(inst.starts_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Artifacts */}
        <EventArtifactsPanel
          clubId={clubId}
          eventId={eventId}
          artifacts={event.artifacts || {}}
          canEdit={canEdit}
          theme={theme}
        />

        {/* Creator info */}
        {event.creator && (
          <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: theme.colors.muted }}>
            {event.creator.avatar_url && (
              <img src={event.creator.avatar_url} alt="" className="h-5 w-5 rounded-full" />
            )}
            Created by {event.creator.full_name}
          </div>
        )}
      </div>
    </div>
  );
}
